/**
 * OrgsTab — Distributor management for OEM admins.
 * Shows distributor orgs linked to the current OEM.
 * Allows creating a new distributor org and editing/toggling existing ones.
 * Also manages machine shares (which machines are white-labelled to each distributor).
 */
import React, { useEffect, useState } from 'react';
import {
  Table, Button, Badge, ActionIcon, Group, Text, TextInput, ColorInput,
  Loader, Alert, Tooltip, Stack, Drawer, Switch, Divider,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import {
  MdEdit, MdAddBusiness, MdSearch, MdRefresh, MdShare,
} from 'react-icons/md';
import {
  getOrgs, createOrg, updateOrg,
  getMachines, getMachineShares, createShare, deleteShare,
} from '../../API/adminAPI';

/* ── Org form modal ──────────────────────────────────────────────────────────── */
const OrgModal = ({ org, onClose }) => {
  const isEdit = Boolean(org);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const form = useForm({
    initialValues: {
      name: org?.name || '',
      logo_url: org?.logo_url || '',
      primary_color: org?.primary_color || '#1F4E79',
      is_active: org?.is_active ?? true,
    },
    validate: {
      name: (v) => (v.trim() ? null : 'Organisation name required'),
    },
  });

  const save = async () => {
    if (form.validate().hasErrors) return;
    setSaving(true);
    setError('');
    try {
      const payload = {
        name: form.values.name.trim(),
        logo_url: form.values.logo_url.trim(),
        primary_color: form.values.primary_color,
        is_active: form.values.is_active,
        org_type: 'distributor',
      };
      if (isEdit) await updateOrg(org.id, payload);
      else await createOrg(payload);
      onClose(true);
    } catch (e) {
      setError(e?.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      opened
      onClose={() => onClose(false)}
      title={isEdit ? `Edit — ${org.name}` : 'New Distributor'}
      size="md"
      position="right"
      padding="xl"
      closeOnClickOutside={false}
    >
      {error && <Alert color="red" mb="sm">{error}</Alert>}
      <Stack>
        <TextInput label="Organisation Name" required {...form.getInputProps('name')} />
        <TextInput
          label="Logo URL"
          placeholder="https://example.com/logo.png"
          description="Shown in the top-bar for all users belonging to this distributor."
          {...form.getInputProps('logo_url')}
        />
        <ColorInput
          label="Brand Colour"
          description="Primary accent colour for the distributor's UI."
          {...form.getInputProps('primary_color')}
        />
        <Switch
          label="Active"
          description="Suspended distributors cannot log in."
          checked={form.values.is_active}
          onChange={(e) => form.setFieldValue('is_active', e.currentTarget.checked)}
        />
      </Stack>
      <Group position="apart" mt="xl">
        <Button variant="subtle" onClick={() => onClose(false)}>Cancel</Button>
        <Button onClick={save} loading={saving}>Save</Button>
      </Group>
    </Drawer>
  );
};

/* ── Share management modal ──────────────────────────────────────────────────── */
const ShareModal = ({ org, onClose }) => {
  const [machines, setMachines] = useState([]);
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadShares = async (machineList) => {
    const list = machineList || machines;
    try {
      const results = await Promise.all(list.map((m) => getMachineShares(m.id).then((sd) => ({ m, sd }))));
      const allShares = [];
      for (const { m, sd } of results) {
        for (const s of (sd.shares || [])) {
          if (s.to_org_id === org.id) allShares.push({ ...s, machine: m });
        }
      }
      setShares(allShares);
    } catch { setShares([]); }
  };

  useEffect(() => {
    getMachines()
      .then((md) => {
        const list = md.machines || [];
        setMachines(list);
        return loadShares(list);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const alreadySharedMachineIds = new Set(shares.map((s) => s.machine.id));
  const unsharedMachines = machines.filter((m) => !alreadySharedMachineIds.has(m.id));

  const addShare = async (machineId) => {
    setSaving(true);
    try {
      await createShare(machineId, { to_org: org.id, permitted_channels: [], is_active: true });
      await loadShares(machines);
    } catch (e) {
      setError(e?.response?.data?.error || 'Share failed');
    } finally { setSaving(false); }
  };

  const removeShare = async (share) => {
    setSaving(true);
    try {
      await deleteShare(share.machine.id, share.id);
      await loadShares(machines);
    } catch (e) {
      setError(e?.response?.data?.error || 'Remove failed');
    } finally { setSaving(false); }
  };

  return (
    <Drawer
      opened
      onClose={() => onClose()}
      title={`Machine Shares — ${org.name}`}
      size="lg"
      position="right"
      padding="xl"
    >
      {error && <Alert color="red" mb="sm">{error}</Alert>}
      {loading ? <Loader /> : (
        <Stack>
          <Text size="sm" color="dimmed">
            Machines shared to this distributor appear under their branding.
            Leave permitted channels empty to share all non-internal channels.
          </Text>

          {shares.length > 0 && (
            <>
              <Text size="xs" weight={600} color="dimmed">SHARED MACHINES</Text>
              {shares.map((s) => (
                <Group key={s.id} position="apart" px={8} py={4}
                  sx={(t) => ({ border: `1px solid ${t.colors.gray[3]}`, borderRadius: t.radius.sm })}>
                  <Group spacing={6}>
                    <Badge variant="outline" color="blue" size="sm">{s.machine.machine_id}</Badge>
                    <Text size="sm">{s.machine.machine_name}</Text>
                    {!s.is_active && <Badge color="red" size="xs">Suspended</Badge>}
                  </Group>
                  <Button variant="subtle" color="red" size="xs" compact
                    loading={saving} onClick={() => removeShare(s)}>
                    Remove
                  </Button>
                </Group>
              ))}
              <Divider />
            </>
          )}

          {unsharedMachines.length > 0 && (
            <>
              <Text size="xs" weight={600} color="dimmed">ADD A MACHINE</Text>
              {unsharedMachines.map((m) => (
                <Group key={m.id} position="apart" px={8} py={4}
                  sx={(t) => ({ border: `1px solid ${t.colors.gray[2]}`, borderRadius: t.radius.sm })}>
                  <Group spacing={6}>
                    <Badge variant="outline" color="gray" size="sm">{m.machine_id}</Badge>
                    <Text size="sm">{m.machine_name}</Text>
                  </Group>
                  <Button variant="light" size="xs" compact
                    loading={saving} onClick={() => addShare(m.id)}>
                    Share
                  </Button>
                </Group>
              ))}
            </>
          )}

          {shares.length === 0 && unsharedMachines.length === 0 && (
            <Text color="dimmed" align="center">No machines available in your scope.</Text>
          )}
        </Stack>
      )}
      <Group position="right" mt="xl">
        <Button onClick={() => onClose()}>Done</Button>
      </Group>
    </Drawer>
  );
};

/* ── Main tab ────────────────────────────────────────────────────────────────── */
const OrgsTab = () => {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [orgModal, setOrgModal] = useState(false);
  const [editOrg, setEditOrg] = useState(null);
  const [shareOrg, setShareOrg] = useState(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getOrgs();
      // Only show distributors in this tab
      setOrgs((data.organisations || []).filter((o) => o.org_type === 'distributor'));
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to load organisations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = orgs.filter((o) =>
    o.name.toLowerCase().includes(search.toLowerCase())
  );

  const onOrgModalClose = (saved) => {
    setOrgModal(false);
    setEditOrg(null);
    if (saved) load();
  };

  if (loading) return <Loader mt="xl" mx="auto" />;

  return (
    <Stack>
      {error && <Alert color="red">{error}</Alert>}

      <Group position="apart">
        <TextInput
          placeholder="Search distributors…"
          icon={<MdSearch />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          sx={{ flex: 1, maxWidth: 320 }}
        />
        <Group spacing="xs">
          <ActionIcon onClick={load} variant="subtle" title="Refresh">
            <MdRefresh size={20} />
          </ActionIcon>
          <Button leftIcon={<MdAddBusiness />} onClick={() => { setEditOrg(null); setOrgModal(true); }}>
            Add Distributor
          </Button>
        </Group>
      </Group>

      <Table striped highlightOnHover>
        <thead>
          <tr>
            <th>Name</th>
            <th>Brand Colour</th>
            <th>Status</th>
            <th style={{ width: 100 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filtered.length === 0 ? (
            <tr><td colSpan={4}>
              <Text color="dimmed" align="center" py="md">No distributors found</Text>
            </td></tr>
          ) : (
            filtered.map((o) => (
              <tr key={o.id}>
                <td>
                  <Group spacing={8}>
                    {o.logo_url && (
                      <img src={o.logo_url} alt="" style={{ height: 24, objectFit: 'contain' }} />
                    )}
                    <Text size="sm" weight={500}>{o.name}</Text>
                  </Group>
                </td>
                <td>
                  <Group spacing={6}>
                    <div style={{
                      width: 16, height: 16, borderRadius: 4,
                      background: o.primary_color || '#1F4E79',
                      border: '1px solid #ccc',
                    }} />
                    <Text size="xs" color="dimmed">{o.primary_color || '—'}</Text>
                  </Group>
                </td>
                <td>
                  <Badge color={o.is_active ? 'green' : 'red'} variant="dot" size="sm">
                    {o.is_active ? 'Active' : 'Suspended'}
                  </Badge>
                </td>
                <td>
                  <Group spacing={4}>
                    <Tooltip label="Edit distributor">
                      <ActionIcon size="sm" variant="subtle"
                        onClick={() => { setEditOrg(o); setOrgModal(true); }}>
                        <MdEdit />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Manage machine shares">
                      <ActionIcon size="sm" variant="subtle" color="blue"
                        onClick={() => setShareOrg(o)}>
                        <MdShare />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      {orgModal && (
        <OrgModal org={editOrg} onClose={onOrgModalClose} />
      )}
      {shareOrg && (
        <ShareModal org={shareOrg} onClose={() => { setShareOrg(null); }} />
      )}
    </Stack>
  );
};

export default OrgsTab;
